import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { paymentProvidersApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  CreditCard, Loader2, Plug, Trash2, Star, Plus, KeyRound, ShieldCheck, X, Webhook, Copy, Check,
} from "lucide-react";

const inputClass =
  "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-colors";

const STATUS_META = {
  connected: { label: "Connected", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  not_connected: { label: "Not connected", cls: "text-slate-600 bg-slate-50 border-slate-200" },
  error: { label: "Connection error", cls: "text-rose-700 bg-rose-50 border-rose-200" },
  expired: { label: "Connection expired", cls: "text-amber-700 bg-amber-50 border-amber-200" },
};

const ENV_LABELS = { test: "Test", live: "Live", sandbox: "Sandbox", production: "Production" };

export function StatusPill({ status, provider }) {
  const m = STATUS_META[status] || STATUS_META.not_connected;
  return (
    <span
      data-testid={`pp-status-${provider}`}
      className={`text-[10.5px] font-semibold px-1.5 py-0.5 rounded border ${m.cls}`}
    >
      {m.label}
    </span>
  );
}

/** Company Settings → Payments: provider-agnostic processor connections. */
export default function PaymentProcessorsCard() {
  const [cat, setCat] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [editing, setEditing] = useState(null); // provider id being connected/managed
  const [busy, setBusy] = useState("");

  const reload = async () => {
    try {
      const [c, a] = await Promise.all([paymentProvidersApi.catalog(), paymentProvidersApi.accounts()]);
      setCat(c);
      setAccounts(a);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load payment processors");
    }
  };
  useEffect(() => { reload(); }, []);

  const connectedIds = useMemo(() => new Set((accounts || []).map((a) => a.provider)), [accounts]);
  const available = useMemo(() => (cat || []).filter((p) => !connectedIds.has(p.id)), [cat, connectedIds]);

  const testConnection = async (provider) => {
    setBusy(`test-${provider}`);
    try {
      const res = await paymentProvidersApi.test(provider);
      if (res.ok) toast.success(res.message || "Connected");
      else toast.error(res.message || "Connection failed");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed");
    } finally {
      setBusy("");
    }
  };

  const makeDefault = async (provider) => {
    setBusy(`default-${provider}`);
    try {
      setAccounts(await paymentProvidersApi.setDefault(provider));
      toast.success("Primary processor updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set primary");
    } finally {
      setBusy("");
    }
  };

  const disconnect = async (provider, name) => {
    if (!window.confirm(`Disconnect ${name}? Saved credentials will be deleted.`)) return;
    setBusy(`disc-${provider}`);
    try {
      setAccounts(await paymentProvidersApi.disconnect(provider));
      if (editing === provider) setEditing(null);
      toast.success(`${name} disconnected`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Disconnect failed");
    } finally {
      setBusy("");
    }
  };

  if (!cat || !accounts) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="payment-processors-card">
        <div className="text-center py-6 text-slate-400"><Loader2 className="h-4 w-4 inline animate-spin" /></div>
      </div>
    );
  }

  const editingSpec = editing ? cat.find((p) => p.id === editing) : null;
  const editingAccount = editing ? accounts.find((a) => a.provider === editing) : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="payment-processors-card">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard className="h-4 w-4 text-indigo-600" />
        <h2 className="text-[14px] font-semibold text-slate-900">Payment processing</h2>
      </div>
      <p className="text-[12px] text-slate-500 mb-4">
        Connect your own merchant account — invoice payments, estimate deposits and customer pay links are charged
        directly to it. Secret keys are stored encrypted and never shown again.
      </p>

      {/* Connected processors */}
      {accounts.length === 0 ? (
        <div
          className="border border-dashed border-slate-200 rounded-lg py-6 text-center text-[12.5px] text-slate-400 italic mb-4"
          data-testid="pp-empty-state"
        >
          No payment processor connected yet — pick one below to get started.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden mb-4">
          {accounts.map((a) => {
            const spec = cat.find((p) => p.id === a.provider) || { name: a.provider };
            return (
              <li key={a.provider} className="px-3.5 py-3 bg-white" data-testid={`pp-account-row-${a.provider}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] font-semibold text-slate-900">{spec.name}</span>
                  <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-500">
                    {ENV_LABELS[a.environment] || a.environment}
                  </span>
                  <StatusPill status={a.status} provider={a.provider} />
                  {a.isDefault ? (
                    <span
                      data-testid={`pp-default-badge-${a.provider}`}
                      className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded border text-indigo-700 bg-indigo-50 border-indigo-200"
                    >
                      <Star className="h-3 w-3 fill-indigo-500 text-indigo-500" /> Primary
                    </span>
                  ) : (
                    <button
                      onClick={() => makeDefault(a.provider)}
                      disabled={!!busy}
                      data-testid={`pp-make-default-${a.provider}`}
                      className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
                    >
                      Make primary
                    </button>
                  )}
                  <div className="ml-auto flex items-center gap-1.5">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => testConnection(a.provider)}
                      disabled={!!busy}
                      data-testid={`pp-test-${a.provider}`}
                      className="h-7 px-2 text-[11.5px] font-semibold"
                    >
                      {busy === `test-${a.provider}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3 mr-1" />}
                      Test
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setEditing(editing === a.provider ? null : a.provider)}
                      data-testid={`pp-manage-${a.provider}`}
                      className="h-7 px-2 text-[11.5px] font-semibold"
                    >
                      Manage
                    </Button>
                    <button
                      onClick={() => disconnect(a.provider, spec.name)}
                      disabled={!!busy}
                      data-testid={`pp-disconnect-${a.provider}`}
                      title="Disconnect"
                      className="h-7 w-7 rounded hover:bg-rose-50 flex items-center justify-center text-slate-400 hover:text-rose-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {(a.accountName || a.accountEmail || a.statusMessage) && (
                  <div className="text-[11.5px] text-slate-400 mt-1">
                    {[a.accountName, a.accountEmail].filter(Boolean).join(" · ")}
                    {a.status === "error" && a.statusMessage ? (
                      <span className="text-rose-500"> {a.statusMessage}</span>
                    ) : null}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Add processor picker */}
      {!editing && available.length > 0 && (
        <div className="flex items-center gap-2">
          <select
            defaultValue=""
            onChange={(e) => e.target.value && setEditing(e.target.value)}
            data-testid="pp-add-provider-select"
            className="h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          >
            <option value="" disabled>Connect a payment processor…</option>
            {available.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <Plus className="h-4 w-4 text-slate-400" />
        </div>
      )}

      {/* Connect / manage form */}
      {editingSpec && (
        <ProviderForm
          key={editing}
          spec={editingSpec}
          account={editingAccount}
          onClose={() => setEditing(null)}
          onSaved={async (testAfter) => {
            await reload();
            if (testAfter) await testConnection(editing);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ProviderForm({ spec, account, onClose, onSaved }) {
  const { user } = useAuth();
  const [env, setEnv] = useState(account?.environment || spec.environments[0]);
  const [connType, setConnType] = useState(account?.connectionType || spec.connectionTypes[0]);
  const [creds, setCreds] = useState(() => {
    const init = {};
    spec.credentialFields.forEach((f) => {
      init[f.key] = f.secret ? "" : account?.credentials?.[f.key] || "";
    });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const webhookUrl = useMemo(() => {
    if (!user?.companyId || !["stripe", "square"].includes(spec.id)) return "";
    const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${backend}/api/webhooks/${spec.id}/${user.companyId}`;
  }, [user?.companyId, spec.id]);

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Webhook URL copied");
    } catch {
      toast.error("Could not copy — copy manually");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { environment: env, connectionType: connType, credentials: {} };
      spec.credentialFields.forEach((f) => {
        const v = (creds[f.key] || "").trim();
        if (f.secret && !v) return; // keep stored secret
        payload.credentials[f.key] = v;
      });
      await paymentProvidersApi.save(spec.id, payload);
      toast.success(`${spec.name} credentials saved`);
      await onSaved(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const liveEnv = env === "live" || env === "production";

  return (
    <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/40 p-4" data-testid="pp-connect-form">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[13px] font-semibold text-slate-900">
          {account ? `Manage ${spec.name}` : `Connect ${spec.name}`}
        </div>
        <button onClick={onClose} data-testid="pp-cancel" className="h-7 w-7 rounded hover:bg-white flex items-center justify-center text-slate-400">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Environment */}
      <div className="mb-3">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Environment</div>
        <div className="flex gap-2">
          {spec.environments.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => setEnv(e)}
              data-testid={`pp-env-${e}`}
              className={`h-8 px-3 rounded-lg border text-[12.5px] font-medium transition-colors ${
                env === e
                  ? e === "live" || e === "production"
                    ? "border-rose-600 bg-rose-600 text-white shadow-sm"
                    : "border-indigo-600 bg-indigo-600 text-white shadow-sm"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {ENV_LABELS[e] || e}
            </button>
          ))}
        </div>
        {liveEnv && (
          <p className="text-[11px] text-rose-600 mt-1">Live/production charges real cards.</p>
        )}
      </div>

      {/* Connection method — only when there's a choice */}
      {spec.connectionTypes.length > 1 && (
        <div className="mb-3">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Connection method</div>
          <div className="flex gap-2">
            {spec.connectionTypes.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setConnType(t)}
                data-testid={`pp-conntype-${t}`}
                className={`h-8 px-3 rounded-lg border text-[12.5px] font-medium transition-colors ${
                  connType === t
                    ? "border-indigo-600 bg-indigo-600 text-white shadow-sm"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {t === "api_credentials" ? "API Credentials" : t === "merchant_account" ? "Merchant Account" : t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dynamic credential fields */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {spec.credentialFields.map((f) => {
          const savedSecret = f.secret && account?.credentials?.[f.key];
          const Icon = f.secret ? KeyRound : ShieldCheck;
          return (
            <div key={f.key}>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1">{f.label}</label>
              <div className="relative">
                <Icon className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type={f.secret ? "password" : "text"}
                  value={creds[f.key]}
                  onChange={(e) => setCreds((c) => ({ ...c, [f.key]: e.target.value }))}
                  data-testid={`pp-field-${f.key}`}
                  placeholder={savedSecret ? "•••••••• (saved — leave blank to keep)" : f.placeholder}
                  className={`${inputClass} pl-8`}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Webhook URL — providers that receive events back need this in their dashboard. */}
      {webhookUrl && (
        <div className="mb-4 rounded-md border border-slate-200 bg-white p-3" data-testid={`pp-webhook-block-${spec.id}`}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Webhook className="h-3.5 w-3.5 text-indigo-500" />
            <div className="text-[11.5px] uppercase tracking-wider font-semibold text-slate-600">Webhook endpoint</div>
          </div>
          <p className="text-[11.5px] text-slate-500 mb-2 leading-relaxed">
            Paste this URL into {spec.name === "Stripe" ? "Stripe → Developers → Webhooks" : "Square → Developer Dashboard → Webhooks"} to auto-sync
            {" "}<span className="font-semibold text-slate-700">payment succeeded / failed / refunded</span> events back to your invoices.
            Copy the signing secret from that dashboard into the field above so we can verify each event&apos;s signature.
          </p>
          <div className="flex items-center gap-1.5">
            <code
              data-testid={`pp-webhook-url-${spec.id}`}
              className="flex-1 text-[11.5px] font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1.5 truncate"
              title={webhookUrl}
            >
              {webhookUrl}
            </code>
            <button
              type="button"
              onClick={copyWebhookUrl}
              data-testid={`pp-webhook-copy-${spec.id}`}
              className="h-7 px-2 rounded border border-slate-200 bg-white hover:bg-slate-50 text-[11.5px] font-semibold text-slate-600 flex items-center gap-1"
            >
              {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="h-9 text-[13px] font-semibold">Cancel</Button>
        <Button
          onClick={save}
          disabled={saving}
          data-testid="pp-save-connect"
          className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] font-semibold"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plug className="h-4 w-4 mr-1.5" />}
          Save &amp; Connect
        </Button>
      </div>
    </div>
  );
}
